import { useCallback } from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import capitalize from 'underscore.string/capitalize'

import GA4 from 'helpers/ga4'

import KeyboardAccessible from 'components/KeyboardAccessible'

import { color } from 'styles/colors'

const propTypes = {
  item: PropTypes.shape({
    seriesName: PropTypes.string.isRequired,
    displayValue: PropTypes.string.isRequired,
    dashSequence: PropTypes.string.isRequired,
    units: PropTypes.func.isRequired
  }).isRequired,
  sectionName: PropTypes.string.isRequired,
  selectedLegendItems: PropTypes.shape().isRequired,
  setSelectedLegendItemsCallback: PropTypes.func.isRequired,
  data: PropTypes.shape()
}

const defaultProps = {
  data: null
}

const LegendGroupItem = ({ item: { seriesName, displayValue, units, dashSequence }, data, sectionName, selectedLegendItems, setSelectedLegendItemsCallback }) => {
  const selected = selectedLegendItems[seriesName]
  const onClick = useCallback(() => {
    const eventData = { category: 'Customer Detail Page', action: 'Change Awair legend option', label: seriesName }

    GA4.sendEvent(eventData)

    setSelectedLegendItemsCallback({
      ...selectedLegendItems,
      [seriesName]: !selected
    })
  }, [setSelectedLegendItemsCallback, seriesName, selectedLegendItems, selected])

  return (
    <S.Item {...{ selected }}>
      <S.Label {...{ selected, onClick }}>
        <svg width="30px" height="5px" xmlns="http://www.w3.org/2000/svg">
          <line x1="0" y1="0" x2="20" y2="0" stroke={color(`${sectionName}${capitalize(seriesName)}`)} strokeWidth="6" strokeDasharray={dashSequence} />
        </svg>
        <span>{displayValue}</span>
      </S.Label>

      {data
        ? (
          <S.HoveredChartValue {...{ selected }}>
            {units(data.y, 1)}
          </S.HoveredChartValue>
        )
        : (
          <S.Checkmark
            {...{ selected, onClick }}
            className="icon-checkmark"
            role="img"
            aria-label={`${displayValue} ${selected ? 'selected' : 'deselected'}`}
          />
        )}
    </S.Item>
  )
}

LegendGroupItem.propTypes = propTypes
LegendGroupItem.defaultProps = defaultProps

export default LegendGroupItem

const S = {
  Item: styled.div`
    position: relative;
    width: 100%;
  `,
  Label: styled.span`
    margin-right: 40px;
    cursor: pointer;

    ${({ selected }) => (selected
    ? css`font-weight: bold;`
    : css`letter-spacing: .31px;

        &:hover {
          letter-spacing: normal;
          font-weight: 600;
        }
      `
  )}
  `,
  // TODO: use text-align: left; when converting thermostat chart to react
  HoveredChartValue: styled.span`
    position: absolute;
    right: 0px;
    min-width: 80px;
    text-align: center;

    ${({ selected }) => selected ? 'font-weight: bold;' : null}
  `,
  Checkmark: styled(KeyboardAccessible)`
    position: absolute;
    padding: 3px;
    top: 2px;
    right: 22px;
    cursor: pointer;
    display: inline;

    ${({ selected }) => (selected
    ? css`
          font-weight: bold;
          color: ${color('selectedLegendCheckmark')};
        `
    : css`
          color: ${color('deselectedLegendCheckmark')};

          &:hover {
            color: ${color('deselectedLegendCheckmarkHover')};
          }
        `
  )}
  `
}
